import type { StudioInfo } from './types';

export const studio: StudioInfo = {
  legalName: 'Inkhaus S.r.l.',
  brand: 'Inkhaus',
  tagline: {
    it: 'Studio di tatuaggi custom. Milano, dal 2017.',
    en: 'Custom tattoo studio. Milan, since 2017.',
  },
  address: { street: 'Via Tortona 35', city: 'Milano', zip: '20144' },
  email: 'studio@inkhaus.it',
  phone: '+39 02 873 891 04',
  instagram: 'inkhaus.studio',
  hours: [
    {
      days: { it: 'Mar — Ven', en: 'Tue — Fri' },
      time: { it: '12:00 — 20:00', en: '12:00 — 20:00' },
    },
    {
      days: { it: 'Sabato', en: 'Saturday' },
      time: { it: '11:00 — 19:00', en: '11:00 — 19:00' },
    },
    {
      days: { it: 'Dom — Lun', en: 'Sun — Mon' },
      time: { it: 'Solo su appuntamento', en: 'By appointment only' },
    },
  ],
  ethos: {
    it: 'Inkhaus non è un negozio. È un atelier in cui quattro tatuatori lavorano con tempi lunghi, riferimenti precisi e una regola: ogni pezzo deve avere il peso di essere fatto. Non accettiamo walk-in. Non disegniamo flash. Non replichiamo lavori altrui.',
    en: 'Inkhaus isn’t a shop. It’s an atelier where four tattooers work with long timelines, precise references and one rule: every piece must carry the weight of being made. No walk-ins. No flash. No replicas of other people’s work.',
  },
  process: [
    {
      step: 1,
      title: { it: 'Richiesta', en: 'Request' },
      body: {
        it: 'Compili il form di prenotazione raccontandoci l’idea, lo stile, una possibile posizione e una rosa di date. Rispondiamo entro 48 ore lavorative con la disponibilità dell’artista giusto.',
        en: 'You fill the booking form telling us the idea, the style, a possible placement and a window of dates. We answer within 48 working hours with the right artist’s availability.',
      },
    },
    {
      step: 2,
      title: { it: 'Consulenza', en: 'Consultation' },
      body: {
        it: 'Una mezz’ora in studio o in videocall: vediamo i riferimenti, parliamo di dimensioni reali, decidiamo se ha senso procedere. La consulenza è gratuita e non vincolante.',
        en: 'Half an hour in studio or on video: we go through references, talk real size, decide if it makes sense to proceed. Consultation is free and non-binding.',
      },
    },
    {
      step: 3,
      title: { it: 'Bozzetto', en: 'Sketch' },
      body: {
        it: 'L’artista disegna il pezzo per te — mai prima della consulenza, mai con AI generativa. Hai una revisione inclusa. Caparra del 30% per fissare la data definitiva.',
        en: 'The artist draws the piece for you — never before consultation, never with generative AI. One revision included. 30% deposit secures the final date.',
      },
    },
    {
      step: 4,
      title: { it: 'Seduta', en: 'Session' },
      body: {
        it: 'In studio: aghi sterili monouso, inchiostri vegani, sedute da 2 a 8 ore secondo il pezzo. Saldo a fine seduta. Istruzioni di guarigione consegnate stampate e via email.',
        en: 'In studio: sterile single-use needles, vegan inks, sessions from 2 to 8 hours depending on the piece. Balance at the end. Aftercare instructions handed printed and emailed.',
      },
    },
  ],
  faq: [
    {
      q: {
        it: 'Quanto costa un tatuaggio da voi?',
        en: 'How much does a tattoo cost here?',
      },
      a: {
        it: 'Il preventivo dipende dall’artista, dalle dimensioni e dalle ore stimate. La tariffa oraria parte da 120 €/h fino a 180 €/h secondo l’artista. Pezzi piccoli (sotto le 2 ore) hanno una tariffa minima di 200 €.',
        en: 'The quote depends on the artist, the size and the estimated hours. Hourly rate ranges from 120 €/h to 180 €/h depending on the artist. Small pieces (under 2 hours) have a 200 € minimum.',
      },
    },
    {
      q: {
        it: 'Quanto tempo prima devo prenotare?',
        en: 'How early should I book?',
      },
      a: {
        it: 'Le agende ruotano su 3-5 mesi. Per pezzi grandi (sleeve, schiene) consigliamo di scrivere con almeno sei mesi di anticipo.',
        en: 'Our calendars run 3 to 5 months ahead. For large pieces (sleeves, backs) we recommend writing at least six months in advance.',
      },
    },
    {
      q: {
        it: 'Posso portarvi un disegno fatto da me?',
        en: 'Can I bring a drawing I made myself?',
      },
      a: {
        it: 'Sì, ma diventa un riferimento, non il file finale. L’artista re-interpreta il disegno nel proprio linguaggio. Se cerchi una copia 1:1 non siamo lo studio giusto.',
        en: 'Yes — but it becomes a reference, not the final file. The artist reinterprets the drawing in their own language. If you want a 1:1 copy, we’re not the right studio.',
      },
    },
    {
      q: {
        it: 'Fate cover-up?',
        en: 'Do you do cover-ups?',
      },
      a: {
        it: 'Solo Davi e Mei accettano cover-up, valutati caso per caso a partire da una foto del tatuaggio esistente.',
        en: 'Only Davi and Mei take cover-ups, evaluated case by case from a photo of the existing tattoo.',
      },
    },
    {
      q: {
        it: 'Posso venire accompagnato?',
        en: 'Can someone come with me?',
      },
      a: {
        it: 'Una persona di accompagnamento è benvenuta. Non possiamo ospitare gruppi: lo studio resta uno spazio di lavoro.',
        en: 'One companion is welcome. We can’t host groups: the studio is a working space.',
      },
    },
    {
      q: {
        it: 'Cosa serve per la prima seduta?',
        en: 'What do I need for the first session?',
      },
      a: {
        it: 'Documento d’identità (maggiorenni), aver mangiato qualcosa nelle 2 ore precedenti, niente alcol nelle 24 ore prima. Pelle non scottata dal sole. Vestiti comodi che lascino accessibile la zona.',
        en: 'ID (18+), having eaten within the last 2 hours, no alcohol in the previous 24. Skin not sunburned. Comfortable clothing leaving the area accessible.',
      },
    },
  ],
  numbers: [
    { value: '8', label: { it: 'Anni in via Tortona', en: 'Years on via Tortona' } },
    { value: '4', label: { it: 'Artisti residenti', en: 'Resident artists' } },
    { value: '2.4k', label: { it: 'Pezzi firmati', en: 'Signed pieces' } },
    { value: '0', label: { it: 'Walk-in accettati', en: 'Walk-ins accepted' } },
  ],
};
